/**
 * BucketGrid — colour-coded region × vehicle-class recommendation table
 */
import type { PricingRecommendation, DepotRegion, VehicleType, RecommendedAction } from '../../types/pricingIntelligence';
import { ALL_REGIONS, ALL_VEHICLE_CLASSES } from '../../types/pricingIntelligence';
import { ACTION_STATUS_ROLE, type StatusRole } from '../../config/colors';

interface Props {
  recommendations: PricingRecommendation[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
}

export function bucketKey(region: DepotRegion, vehicleClass: VehicleType): string {
  return `${region}::${vehicleClass}`;
}

// Role → bordered-chip class shape (this table's expression). The
// action → role assignment itself lives in config/colors.ts
// (ACTION_STATUS_ROLE) so it is defined exactly once.
const ROLE_CHIP: Record<StatusRole | 'neutral', { bg: string; text: string }> = {
  success:     { bg: 'bg-success/60 border-success/50',       text: 'text-success' },
  destructive: { bg: 'bg-destructive/60 border-destructive/50', text: 'text-destructive' },
  warning:     { bg: 'bg-warning/60 border-warning/50',       text: 'text-warning' },
  info:        { bg: 'bg-ring/60 border-ring/50',             text: 'text-ring' },
  neutral:     { bg: 'bg-secondary/60 border-border/50',      text: 'text-muted-foreground' },
};

const ACTION_STYLES: Record<RecommendedAction, { bg: string; text: string; label: string }> = {
  increase: { ...ROLE_CHIP[ACTION_STATUS_ROLE.increase], label: 'PUSH' },
  decrease: { ...ROLE_CHIP[ACTION_STATUS_ROLE.decrease], label: 'DISC' },
  hold:     { ...ROLE_CHIP[ACTION_STATUS_ROLE.hold],     label: 'HOLD' },
  review:   { ...ROLE_CHIP[ACTION_STATUS_ROLE.review],   label: 'WATCH' },
};

function fmt(pct: number): string {
  return (pct >= 0 ? '+' : '') + pct.toFixed(1) + '%';
}

function ConfidenceDots({ value }: { value: number }) {
  const filled = Math.round(value * 5);
  return (
    <div className="flex gap-0.5 mt-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className={`w-1 h-1 rounded-full ${i < filled ? 'bg-current opacity-70' : 'bg-accent'}`}
        />
      ))}
    </div>
  );
}

export default function BucketGrid({ recommendations, selectedKey, onSelect }: Props) {
  const byKey = new Map<string, PricingRecommendation>();
  for (const r of recommendations) {
    byKey.set(bucketKey(r.regionId, r.vehicleClass), r);
  }

  // Only show columns that appear in at least one recommendation
  const activeClasses = ALL_VEHICLE_CLASSES.filter(vc =>
    ALL_REGIONS.some(r => byKey.has(bucketKey(r, vc)))
  );
  const activeRegions = ALL_REGIONS.filter(region =>
    activeClasses.some(vc => byKey.has(bucketKey(region, vc)))
  );

  if (activeClasses.length === 0) {
    return <p className="text-sm text-muted-foreground">No recommendations to display.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-separate border-spacing-1">
        <thead>
          <tr>
            <th className="text-left text-muted-foreground font-normal px-2 py-1 w-24">Region</th>
            {activeClasses.map(vc => (
              <th key={vc} className="text-center text-muted-foreground font-medium px-1 py-1 min-w-23">
                {vc}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {activeRegions.map(region => (
            <tr key={region}>
              <td className="text-muted-foreground px-2 py-1 text-right whitespace-nowrap">{region}</td>
              {activeClasses.map(vc => {
                const rec = byKey.get(bucketKey(region, vc));
                const key = bucketKey(region, vc);
                const isSelected = selectedKey === key;

                if (!rec) {
                  return (
                    <td key={vc} className="px-1 py-1">
                      <div className="rounded border border-border/30 bg-card/20 h-14 flex items-center justify-center">
                        <span className="text-muted-foreground">—</span>
                      </div>
                    </td>
                  );
                }

                const style = ACTION_STYLES[rec.recommendedAction];
                return (
                  <td key={vc} className="px-1 py-1">
                    <button
                      onClick={() => onSelect(key)}
                      className={`w-full rounded border ${style.bg} ${style.text} ${
                        isSelected ? 'ring-2 ring-white/30' : ''
                      } h-14 flex flex-col items-center justify-center gap-0 cursor-pointer hover:brightness-125 transition-all`}
                    >
                      <span className="font-bold text-xs tracking-wide">{style.label}</span>
                      <span className="font-mono text-xs">{fmt(rec.recommendedAdjustmentPct)}</span>
                      <ConfidenceDots value={rec.confidence} />
                      {rec.reviewRequired && (
                        <span className="text-warning text-xs leading-none mt-0.5">⚠ review</span>
                      )}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Legend */}
      <div className="flex gap-3 mt-3 flex-wrap">
        {(Object.entries(ACTION_STYLES) as Array<[RecommendedAction, typeof ACTION_STYLES[RecommendedAction]]>).map(
          ([action, s]) => (
            <div key={action} className={`flex items-center gap-1.5 rounded px-2 py-1 border ${s.bg}`}>
              <span className={`text-xs font-bold ${s.text}`}>{s.label}</span>
              <span className="text-muted-foreground text-xs capitalize">{action}</span>
            </div>
          )
        )}
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground ml-auto">
          <span className="text-muted-foreground">·</span> dots = confidence (5 = highest)
        </div>
      </div>
    </div>
  );
}
